import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useOrganization, CreateOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';
import type { MethodologyTemplate } from '../types';

interface SubscriptionInfo {
  type: 'individual' | 'organization';
  tier: string;
  status: string;
  seatCount?: number;
  maxSeats?: number;
}

interface BillingStatus {
  enabled: boolean;
  publishableKey: string | null;
}

export function Settings() {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { get, post } = useApi();

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [methodologies, setMethodologies] = useState<MethodologyTemplate[]>([]);
  const [expandedMethodology, setExpandedMethodology] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [subResponse, billingResponse] = await Promise.all([
          get<SubscriptionInfo>('/billing/subscription'),
          get<BillingStatus>('/billing/status'),
        ]);
        setSubscription(subResponse);
        setBillingStatus(billingResponse);
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();

    // Fetch methodologies
    get<{ methodologies: MethodologyTemplate[] }>('/methodologies')
      .then(data => setMethodologies(data.methodologies))
      .catch(err => console.warn('Failed to load methodologies:', err));
  }, []);

  async function handleUpgrade(plan: 'individual' | 'team', interval: 'monthly' | 'yearly') {
    try {
      setCheckoutLoading(true);
      const response = await post<{ url: string }>('/billing/checkout', { plan, interval });
      window.location.href = response.url;
    } catch (err) {
      alert('Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  }

  async function openBillingPortal() {
    try {
      const response = await get<{ url: string }>('/billing/portal');
      window.location.href = response.url;
    } catch (err) {
      alert('Failed to open billing portal');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="mt-1 text-slate-400">Manage your account and subscription</p>
      </div>

      {/* Profile section */}
      <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h2 className="text-lg font-medium text-white mb-4">Profile</h2>
        <div className="flex items-center space-x-4">
          {user?.imageUrl && (
            <img
              src={user.imageUrl}
              alt={user.fullName || 'Profile'}
              className="w-16 h-16 rounded-full"
            />
          )}
          <div>
            <p className="text-white font-medium">{user?.fullName || 'User'}</p>
            <p className="text-slate-400">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
        </div>
      </section>

      {/* Current subscription */}
      <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h2 className="text-lg font-medium text-white mb-4">Current Plan</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-green-500 capitalize">
              {subscription?.tier || 'Free'}
            </p>
            <p className="text-slate-400">
              {subscription?.status === 'active' ? 'Active subscription' : 'No active subscription'}
            </p>
            {subscription?.type === 'organization' && (
              <p className="text-sm text-slate-500 mt-1">
                Organization: {organization?.name} ({subscription.seatCount}/{subscription.maxSeats} seats)
                {' · '}
                <Link to="/settings/members" className="text-green-500 hover:text-green-400">
                  Manage members
                </Link>
              </p>
            )}
          </div>
          {subscription?.tier !== 'free' && (
            <button
              onClick={openBillingPortal}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md"
            >
              Manage Billing
            </button>
          )}
        </div>
      </section>

      {/* Sales Frameworks */}
      {methodologies.length > 0 && (
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-lg font-medium text-white mb-1">Sales Frameworks</h2>
          <p className="text-slate-400 text-sm mb-4">
            Choose a framework when starting a session to score your calls against proven sales methodologies.
          </p>
          <div className="grid gap-3">
            {methodologies.map(m => (
              <div
                key={m.id}
                className="bg-slate-700/50 rounded-lg border border-slate-600 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedMethodology(expandedMethodology === m.id ? null : m.id)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-700/80 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-white">{m.name}</h3>
                      <div className="flex items-center gap-1.5">
                        {m.category && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            m.category === 'qualification' ? 'bg-blue-900/50 text-blue-400' :
                            m.category === 'discovery' ? 'bg-emerald-900/50 text-emerald-400' :
                            'bg-orange-900/50 text-orange-400'
                          }`}>
                            {m.category}
                          </span>
                        )}
                        {m.complexity && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600 text-slate-300">
                            {m.complexity}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{m.elements.length} elements</span>
                      <svg
                        className={`w-4 h-4 text-slate-400 transition-transform ${expandedMethodology === m.id ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{m.description}</p>
                  {m.bestFor && (
                    <p className="text-xs text-slate-500 mt-1">Best for: {m.bestFor}</p>
                  )}
                </button>

                {expandedMethodology === m.id && (
                  <div className="px-4 pb-4 border-t border-slate-600">
                    <div className="mt-3 space-y-2">
                      {m.elements.map(el => (
                        <div key={el.code} className="flex items-start gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-purple-900/40 text-purple-400 text-xs font-bold flex-shrink-0 mt-0.5">
                            {el.code}
                          </span>
                          <div>
                            <span className="text-sm text-slate-200 font-medium">{el.name}</span>
                            <p className="text-xs text-slate-500">{el.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {m.source && (
                      <p className="text-xs text-slate-600 mt-3">Source: {m.source}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upgrade options */}
      {subscription?.tier === 'free' && billingStatus?.enabled && (
        <section>
          <h2 className="text-lg font-medium text-white mb-4">Upgrade Your Plan</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Individual plan */}
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
              <h3 className="text-xl font-bold text-white">Individual</h3>
              <p className="text-slate-400 mt-1">Perfect for solo presenters</p>
              <p className="mt-4">
                <span className="text-3xl font-bold text-white">$5</span>
                <span className="text-slate-400">/month</span>
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li>✓ Unlimited sessions</li>
                <li>✓ Real-time tracking</li>
                <li>✓ LLM agenda expansion</li>
                <li>✓ Unlimited templates</li>
              </ul>
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => handleUpgrade('individual', 'monthly')}
                  disabled={checkoutLoading}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  {checkoutLoading ? 'Loading...' : 'Subscribe Monthly'}
                </button>
                <button
                  onClick={() => handleUpgrade('individual', 'yearly')}
                  disabled={checkoutLoading}
                  className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-md disabled:opacity-50"
                >
                  $50/year (Save $10)
                </button>
              </div>
            </div>

            {/* Team plan */}
            <div className="bg-slate-800 rounded-lg p-6 border border-green-500/50">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Team</h3>
                <span className="px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                  Popular
                </span>
              </div>
              <p className="text-slate-400 mt-1">For sales teams</p>
              <p className="mt-4">
                <span className="text-3xl font-bold text-white">$10</span>
                <span className="text-slate-400">/user/month</span>
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li>✓ Everything in Individual</li>
                <li>✓ Session history & analytics</li>
                <li>✓ Transcript storage</li>
                <li>✓ Team management</li>
                <li>✓ Shared templates</li>
              </ul>
              <div className="mt-6 space-y-2">
                {organization ? (
                  <>
                    <button
                      onClick={() => handleUpgrade('team', 'monthly')}
                      disabled={checkoutLoading}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                    >
                      {checkoutLoading ? 'Loading...' : 'Subscribe Monthly'}
                    </button>
                    <button
                      onClick={() => handleUpgrade('team', 'yearly')}
                      disabled={checkoutLoading}
                      className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-md disabled:opacity-50"
                    >
                      $100/user/year (Save $20)
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowCreateOrg(true)}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md"
                    >
                      Create Organization
                    </button>
                    <p className="text-center text-slate-500 text-xs">
                      Or join an existing organization via invite
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Members - Organization only */}
      {organization && (
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-lg font-medium text-white mb-4">Members</h2>
          <p className="text-slate-400 text-sm">
            Invite and manage organization members. Control roles and permissions
            for your team.
          </p>
          <div className="mt-4">
            <Link
              to="/settings/members"
              className="inline-flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Manage Members
            </Link>
          </div>
        </section>
      )}

      {/* Teams - Organization only */}
      {organization && (
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-lg font-medium text-white mb-4">Teams</h2>
          <p className="text-slate-400 text-sm">
            Organize members into teams for better collaboration and analytics.
            Teams help managers track performance by group.
          </p>
          <div className="mt-4">
            <Link
              to="/settings/teams"
              className="inline-flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Manage Teams
            </Link>
          </div>
        </section>
      )}

      {/* Data & Privacy */}
      <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h2 className="text-lg font-medium text-white mb-4">Data & Privacy</h2>
        <p className="text-slate-400 text-sm">
          Your session data is stored securely. For Team plans, transcripts are retained for 1 year.
          Individual plans do not store session history.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-4">
          <Link
            to="/settings/privacy"
            className="inline-flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Data Contribution Settings
          </Link>
          <a
            href="/privacy"
            target="_blank"
            className="inline-flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Privacy Policy
          </a>
        </div>
      </section>

      {/* Organization Vocabulary - Team tier only */}
      {subscription?.tier === 'team' && organization && (
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-lg font-medium text-white mb-4">Organization Vocabulary</h2>
          <p className="text-slate-400 text-sm">
            Create custom vocabulary mappings to improve speech recognition accuracy for your team's
            industry-specific terms, product names, and jargon.
          </p>
          <div className="mt-4">
            <Link
              to="/org/vocabulary"
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Manage Vocabulary
            </Link>
          </div>
        </section>
      )}

      {/* Knowledge Base - Team tier only */}
      {subscription?.tier === 'team' && organization && (
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-lg font-medium text-white mb-4">Knowledge Base</h2>
          <p className="text-slate-400 text-sm">
            Add your product docs, playbooks, and reference materials. Powers fact-checking
            and live document lookup during calls.
          </p>
          <div className="mt-4">
            <Link
              to="/org/documentation"
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Manage Knowledge Base
            </Link>
          </div>
        </section>
      )}

      {/* Create Organization Modal */}
      {showCreateOrg && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowCreateOrg(false)}
              className="absolute -top-10 right-0 text-white hover:text-slate-300 text-sm"
            >
              ✕ Close
            </button>
            <CreateOrganization
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'shadow-xl rounded-xl border border-slate-700 bg-slate-800',
                  headerTitle: 'text-white',
                  headerSubtitle: 'text-slate-400',
                  formFieldLabel: 'text-slate-300',
                  formFieldInput: 'bg-slate-700 border-slate-600 text-white focus:ring-green-500 focus:border-green-500',
                  formButtonPrimary: 'bg-green-500 hover:bg-green-600',
                  footerActionLink: 'text-green-400 hover:text-green-300',
                },
              }}
              afterCreateOrganizationUrl="/settings"
            />
          </div>
        </div>
      )}
    </div>
  );
}
